<template>
  <div v-if="error" class="q-mb-md">
    <q-banner class="text-white bg-red" rounded>
      {{ error.message }}
    </q-banner>
  </div>

  <div class="text-h6 q-mb-md">
    {{ $t('components.doctorAccountTab.sections.account') }}
  </div>

  <div class="row q-col-gutter-md">
    <div class="col-12 col-md-6">
      <q-input
        v-model.trim="localDoctor.user.fullName"
        :label="$t('components.doctorAccountTab.labels.fullName')"
        :error="v$.localDoctor.user.fullName.$error"
        :error-message="$t('components.doctorAccountTab.errors.fullName')"
        :readonly="!isEdit"
        hide-bottom-space
      />
    </div>

    <div class="col-12 col-md-6">
      <q-input
        v-model.trim="localDoctor.user.email"
        :label="$t('components.doctorAccountTab.labels.email')"
        :error="v$.localDoctor.user.email.$error"
        :error-message="$t('components.doctorAccountTab.errors.email')"
        :readonly="!isEdit"
        hide-bottom-space
      />
    </div>

    <div class="col-12 col-md-6">
      <q-input
        v-model.trim="localDoctor.user.password"
        :label="$t('components.doctorAccountTab.labels.password')"
        :error="v$.localDoctor.user.password.$error"
        :error-message="$t('components.doctorAccountTab.errors.password')"
        :readonly="!isEdit"
        type="password"
        hide-bottom-space
      />
    </div>
  </div>

  <div class="text-h6 q-mb-md">
    {{ $t('components.doctorAccountTab.sections.contacts') }}
  </div>

  <div class="row q-col-gutter-md">
    <div class="col-12 col-md-6">
      <q-select
        v-model="localDoctor.user.country"
        :options="dealerCountries"
        :label="$t('components.doctorAccountTab.labels.country')"
        :error="v$.localDoctor.user.country.$error"
        :error-message="$t('components.doctorAccountTab.errors.country')"
        :readonly="!isEdit"
        hide-bottom-space
      />
    </div>

    <div class="col-12 col-md-6">
      <div class="row q-col-gutter-md">
        <div class="col-12 col-md-6">
          <q-input
            v-model.trim="localDoctor.user.city"
            :label="$t('components.doctorAccountTab.labels.city')"
            :error="v$.localDoctor.user.city.$error"
            :error-message="$t('components.doctorAccountTab.errors.city')"
            :readonly="!isEdit"
            hide-bottom-space
          />
        </div>

        <div class="col-12 col-md-6">
          <q-input
            v-model.trim="localDoctor.user.postalCode"
            :label="$t('components.doctorAccountTab.labels.postalCode')"
            :error="v$.localDoctor.user.postalCode.$error"
            :error-message="$t('components.doctorAccountTab.errors.postalCode')"
            :readonly="!isEdit"
            hide-bottom-space
          />
        </div>
      </div>
    </div>

    <div class="col-12 col-md-6">
      <q-input
        v-model.trim="localDoctor.user.address"
        :label="$t('components.doctorAccountTab.labels.address')"
        :error="v$.localDoctor.user.address.$error"
        :error-message="$t('components.doctorAccountTab.errors.address')"
        :readonly="!isEdit"
        hide-bottom-space
      />
    </div>

    <div class="col-12 col-md-6">
      <q-input
        v-model.trim="localDoctor.user.contactPhone"
        :label="$t('components.doctorAccountTab.labels.contactPhone')"
        :error="v$.localDoctor.user.contactPhone.$error"
        :error-message="$t('components.doctorAccountTab.errors.contactPhone')"
        :readonly="!isEdit"
        hide-bottom-space
      />
    </div>
  </div>

  <div class="row q-col-gutter-md q-mt-sm">
    <div class="col-12 col-md-6">
      <q-checkbox
        v-model="localDoctor.user.isActive"
        :label="$t('components.doctorAccountTab.labels.status')"
        :disable="!isEdit"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import useValidate from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';

import { updateDoctor } from '@/api/doctors';

export default {
  props: {
    doctor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      error: null,
      v$: useValidate(),
      isEdit: false,
      localDoctor: _.cloneDeep(this.doctor),
    };
  },
  validations: {
    localDoctor: {
      user: {
        fullName: {
          required,
        },
        email: {
          required,
          email,
        },
        password: {},
        country: {},
        city: {},
        postalCode: {},
        address: {},
        contactPhone: {},
      },
    },
  },
  computed: {
    ...mapGetters({
      dealerCountries: 'account/countries',
    }),
  },
  created() {
    this.updateTitle();
    this.updateButtons();
  },
  watch: {
    isEdit() {
      this.updateButtons();
    },
  },
  methods: {
    updateTitle() {
      this.$emit('update:title', this.localDoctor.user.fullName);
    },
    updateButtons() {
      this.$emit('update:buttons', [
        {
          isAllowed: this.$isAllowed('dealer/doctors/update'),
          color: this.isEdit ? 'secondary' : 'primary',
          label: this.isEdit
            ? this.$t('components.doctorAccountTab.saveButtonTitle')
            : this.$t('components.doctorAccountTab.editButtonTitle'),
          isOutline: !this.isEdit,
          onClick: this.isEdit ? this.save : this.edit,
        },
      ]);
    },
    edit() {
      this.isEdit = true;
    },
    async save() {
      this.error = null;

      this.v$.$touch();

      if (!this.v$.$invalid) {
        try {
          const doctor = await updateDoctor(this.localDoctor);

          this.$emit('update:doctor', doctor);
          this.updateTitle();

          this.isEdit = false;
        } catch (error) {
          console.log(error);

          this.error = error;
        }
      }
    },
  },
};
</script>
